/* eslint-disable react/jsx-no-comment-textnodes */
import type { ReactNode, ReactElement } from 'react';

import { Quill } from 'react-quill-new';
import { Icon } from '@iconify/react';
import ReactDOMServer from 'react-dom/server';
import codeFill from '@iconify/icons-eva/code-fill';
import roundUndo from '@iconify/icons-ic/round-undo';
import roundRedo from '@iconify/icons-ic/round-redo';
import link2Fill from '@iconify/icons-eva/link-2-fill';
import image2Fill from '@iconify/icons-eva/image-2-fill';
import roundSubscript from '@iconify/icons-ic/round-subscript';
import roundFunctions from '@iconify/icons-ic/round-functions';
import roundFormatBold from '@iconify/icons-ic/round-format-bold';
import roundSuperscript from '@iconify/icons-ic/round-superscript';
import roundLocalMovies from '@iconify/icons-ic/round-local-movies';
import roundFormatQuote from '@iconify/icons-ic/round-format-quote';
import roundFormatClear from '@iconify/icons-ic/round-format-clear';
import roundFormatItalic from '@iconify/icons-ic/round-format-italic';
import roundFormatColorFill from '@iconify/icons-ic/round-format-color-fill';
import roundFormatColorText from '@iconify/icons-ic/round-format-color-text';
import roundFormatAlignLeft from '@iconify/icons-ic/round-format-align-left';
import roundFormatUnderlined from '@iconify/icons-ic/round-format-underlined';
import roundFormatAlignRight from '@iconify/icons-ic/round-format-align-right';
import roundFormatAlignCenter from '@iconify/icons-ic/round-format-align-center';
import roundFormatListBulleted from '@iconify/icons-ic/round-format-list-bulleted';
import roundFormatListNumbered from '@iconify/icons-ic/round-format-list-numbered';
import roundFormatAlignJustify from '@iconify/icons-ic/round-format-align-justify';
import roundFormatStrikethrough from '@iconify/icons-ic/round-format-strikethrough';
import roundFormatIndentDecrease from '@iconify/icons-ic/round-format-indent-decrease';
import roundFormatIndentIncrease from '@iconify/icons-ic/round-format-indent-increase';
import roundFormatTextdirectionLToR from '@iconify/icons-ic/round-format-textdirection-l-to-r';
import roundFormatTextdirectionRToL from '@iconify/icons-ic/round-format-textdirection-r-to-l';
import QuillEditorToolbarStyle from './QuillEditorToolbarStyle';
import { useTranslation } from 'react-i18next';

export interface QuillEditorToolbarProps {
  id?: string;
  isSimple?: boolean;
  [key: string]: any;
}

// ----------------------------------------------------------------------

const FONT_SIZE = [
  '8px',
  '9px',
  '10px',
  '12px',
  '14px',
  '16px',
  '20px',
  '24px',
  '32px',
  '42px',
  '54px',
  '68px',
  '84px',
  '98px',
];
const HEADINGS = [
  'Heading 1',
  'Heading 2',
  'Heading 3',
  'Heading 4',
  'Heading 5',
  'Heading 6',
];

const Size = Quill.import('attributors/style/size');
// @ts-ignore legacy boundary
Size.whitelist = FONT_SIZE;
// @ts-ignore legacy boundary
Quill.register(Size, true);

const Icons = Quill.import('ui/icons');
function renderIcon(icon) {
  return ReactDOMServer.renderToString(
    <Icon icon={icon} width={18} height={18} />
  );
}

// @ts-ignore legacy boundary
Icons.bold = renderIcon(roundFormatBold);
// @ts-ignore legacy boundary
Icons.italic = renderIcon(roundFormatItalic);
// @ts-ignore legacy boundary
Icons.underline = renderIcon(roundFormatUnderlined);
// @ts-ignore legacy boundary
Icons.strike = renderIcon(roundFormatStrikethrough);
// @ts-ignore legacy boundary
Icons.link = renderIcon(link2Fill);
// @ts-ignore legacy boundary
Icons.image = renderIcon(image2Fill);
// @ts-ignore legacy boundary
Icons.code = renderIcon(image2Fill);
Icons['code-block'] = renderIcon(codeFill);
// @ts-ignore legacy boundary
Icons.list.bullet = renderIcon(roundFormatListBulleted);
// @ts-ignore legacy boundary
Icons.list.ordered = renderIcon(roundFormatListNumbered);
// @ts-ignore legacy boundary
Icons.indent['+1'] = renderIcon(roundFormatIndentIncrease);
// @ts-ignore legacy boundary
Icons.indent['-1'] = renderIcon(roundFormatIndentDecrease);
// @ts-ignore legacy boundary
Icons.script.sub = renderIcon(roundSubscript);
// @ts-ignore legacy boundary
Icons.script.super = renderIcon(roundSuperscript);
// @ts-ignore legacy boundary
Icons.direction[''] = renderIcon(roundFormatTextdirectionLToR);
// @ts-ignore legacy boundary
Icons.direction.rtl = renderIcon(roundFormatTextdirectionRToL);
// @ts-ignore legacy boundary
Icons.color = renderIcon(roundFormatColorText);
// @ts-ignore legacy boundary
Icons.background = renderIcon(roundFormatColorFill);
// @ts-ignore legacy boundary
Icons.video = renderIcon(roundLocalMovies);
// @ts-ignore legacy boundary
Icons.blockquote = renderIcon(roundFormatQuote);
// @ts-ignore legacy boundary
Icons.clean = renderIcon(roundFormatClear);
// @ts-ignore legacy boundary
Icons.formula = renderIcon(roundFunctions);
// @ts-ignore legacy boundary
Icons.align[''] = renderIcon(roundFormatAlignLeft);
// @ts-ignore legacy boundary
Icons.align.center = renderIcon(roundFormatAlignCenter);
// @ts-ignore legacy boundary
Icons.align.justify = renderIcon(roundFormatAlignJustify);
// @ts-ignore legacy boundary
Icons.align.right = renderIcon(roundFormatAlignRight);

export const undoChange = function () {
  this.quill.history.undo();
};

export const redoChange = function () {
  this.quill.history.redo();
};

export const linkChange = function (value) {
  if (value) {
    const range = this.quill.getSelection();
    if (range == null || range.length === 0) return;
    let preview = this.quill.getText(range);
    if (/^\S+@\S+\.\S+$/.test(preview) && preview.indexOf('mailto:') !== 0) {
      preview = `mailto:${preview}`;
    }

    const { tooltip } = this.quill.theme;
    let input = tooltip.root.querySelector('input[data-link]');
    input.dataset.link = 'https://careit.com';

    tooltip.edit('link', preview);
  } else {
    this.quill.format('link', false);
  }
};

export const formats = [
  'align',
  'background',
  'blockquote',
  'bold',
  'code',
  'code-block',
  'color',
  'direction',
  'font',
  'formula',
  'header',
  'image',
  'indent',
  'italic',
  'link',
  'list',
  'script',
  'size',
  'strike',
  'table',
  'underline',
  'video',
];

export default function QuillEditorToolbar({
  id,
  isSimple,
  ...other
}: QuillEditorToolbarProps) {
  const { t } = useTranslation();
  return (
    <QuillEditorToolbarStyle {...other}>
      <div id={id}>
        <div className="ql-formats">
          {!isSimple && (
            <select className="ql-size" defaultValue="16px">
              {FONT_SIZE.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          )}

          <select className="ql-header" defaultValue="">
            {HEADINGS.map((heading, index) => (
              <option key={heading} value={index + 1}>
                {t('editor.heading', { level: index + 1 })}
              </option>
            ))}
            <option value="">{t('editor.normal')}</option>
          </select>
        </div>

        <div className="ql-formats">
          <button type="button" className="ql-bold" />
          <button type="button" className="ql-italic" />
          <button type="button" className="ql-underline" />
          <button type="button" className="ql-strike" />
        </div>

        {!isSimple && (
          <div className="ql-formats">
            <select className="ql-color" />
            <select className="ql-background" />
          </div>
        )}

        <div className="ql-formats">
          <button type="button" className="ql-list" value="ordered" />
          <button type="button" className="ql-list" value="bullet" />
          {!isSimple && (
            <button type="button" className="ql-indent" value="-1" />
          )}
          {!isSimple && (
            <button type="button" className="ql-indent" value="+1" />
          )}
        </div>

        {!isSimple && (
          <div className="ql-formats">
            <button type="button" className="ql-script" value="super" />
            <button type="button" className="ql-script" value="sub" />
          </div>
        )}

        {!isSimple && (
          <div className="ql-formats">
            {/* <button type="button" className="ql-code-block" /> */}
            <button type="button" className="ql-blockquote" />
          </div>
        )}

        <div className="ql-formats">
          <button type="button" className="ql-direction" value="rtl" />
          <select className="ql-align" />
        </div>

        <div className="ql-formats">
          <button type="button" className="ql-link" />
          <button type="button" className="ql-image" />
          <button type="button" className="ql-video" />
        </div>

        <div className="ql-formats">
          {/* !isSimple && <button type="button" className="ql-formula" /> */}
          <button type="button" className="ql-clean" />
        </div>

        {!isSimple && (
          <div className="ql-formats">
            <button type="button" className="ql-undo">
              <Icon icon={roundUndo} width={18} height={18} />
            </button>
            <button type="button" className="ql-redo">
              <Icon icon={roundRedo} width={18} height={18} />
            </button>
          </div>
        )}
      </div>
    </QuillEditorToolbarStyle>
  );
}
